import React, { useState, useEffect } from 'react'
import { ProgressBar } from '../../components/ProgressBar/ProgressBar'
import { FooterEventEdit } from '../../components/Footer/FooterEventEdit'
import Typography from '@mui/material/Typography'
import {
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Grid,
} from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import { root } from '../../global'
import { useNavigate, useParams } from 'react-router-dom'
import { Event, EventStatus, EventType } from '../../services/Types'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { BasicInfoReview } from './components/BasicInfoReview'
import { EventInfoReview } from './components/EventInfoReview'
import { SpeakerAndStaffSupportReview } from './components/SpeakerAndStaffSupportReview'
import { AudienceReview } from './components/AudienceReview'
import { DateAndTimesReview } from './components/DateAndTimesReview'
import { FeaturesAndRequestsReview } from './components/FeaturesAndRequestsReview'
import { copyObj, formatDate } from '../../services/Helper'
import { GoToSectionButton } from '../../components/GoToSectionButton/GoToSectionButton'
import { useEvent } from '../../hooks/useEvent'
import { BadgeEventChanges } from '../../components/BadgeEventChanges/BadgeEventChanges'
import { usePermissions } from '../../hooks/usePermissions'
import { EventValidationProvider } from '../../contexts/EventValidationContext'

// Props for all the review components
export interface EventReviewProps {
  eventData: Event | undefined;
  handleEventChange: (field: keyof Event) => (e: React.ChangeEvent<HTMLInputElement>) => void;
}




export default function EventReview() {
  let { id } = useParams()
  const navigate = useNavigate()
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [showServerError, setShowServerError] = useState(false)
  const [serverError, setServerError] = useState('')
  const [serverInfo, setServerInfo] = useState('')

  const {
    errorUseEvent,
    eventData,
    setEventData,
    getEventData,
    resetCache,
  } = useEvent();
  const { eventStaffIsComplete, eventAudienceIsComplete, eventBookingIsComplete,
    eventInfoIsComplete, eventBasicInfoIsComplete, eventFeaturesAndRequestsIsComplete } = usePermissions(eventData || {} as Event);

  useEffect(() => {
    resetCache(Number(id));
    getEventData(Number(id));
  }, [id])


  useEffect(() => {
    showSuccessMessage &&
      setTimeout(() => {
        setShowSuccessMessage(false)
        setServerInfo('')
      }, 5000)
  }, [showSuccessMessage, navigate])

  useEffect(() => {
    showServerError &&
      setTimeout(() => {
        setShowServerError(false)
      }, 5000)
  }, [showServerError])

  const displayServerError = () => {
    setShowServerError(true)
    setServerError('Internal server error')
  }

  const handleEventChange = (field: keyof Event) => async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    try {
      if (!eventData?.id) throw new Error('No event id')

      // create a new event object with the updated field so we can pass it to the async function
      const eventValue = e.target.value;
      let newEvent: Event = copyObj(eventData)
      newEvent = {
        ...newEvent,
        [field]: eventValue
      };

      // The levelOneApprover and programOffice are delivered as objects,
      // so we need to convert them to numbers/IDs
      newEvent.levelOneApproverId = newEvent.levelOneApprover?.id || null
      newEvent.programOfficeId = newEvent.programOffice?.id || null

      if (newEvent.eventType === EventType.BLOCKED) {
        newEvent.status = EventStatus.BLOCKED;
      }
      setEventData(newEvent);
    } catch (error) {
      console.error(error)
      setServerError('Error updating event info: ' + error)
    }
  }

  if (!eventData) {
    return (
      <Box style={root} sx={{ background: '#F9FBFC' }}>
        {errorUseEvent && <Grid item xs={12}><Alert severity="error">{errorUseEvent}</Alert></Grid>}
        <div>Loading...</div>
      </Box>
    )
  }

  // if the path is /event/review/:id, then show the progress bar
  const showProgressBar = window.location.pathname.startsWith('/event/review/')
    && eventData.status === EventStatus.SAVED;

  const blockEvent = eventData.status === EventStatus.BLOCKED || eventData.eventType === EventType.BLOCKED;
  const showAudience = eventData.eventType === EventType.WEBINAR;

  const renderWarningIcon = () => (
    <WarningIcon sx={{ fontSize: '28px', color: 'red' }} />
  );

  return (
    <EventValidationProvider>
    <Box style={root}>
      {showProgressBar && (
        <ProgressBar
          activePageTitle='Review & Submit'
          requestType={eventData.eventType}
        />
      )}

      <Typography variant="h1">
        Event Review
      </Typography>

      {errorUseEvent && <Grid item xs={12}><Alert severity="error">{errorUseEvent}</Alert></Grid>}
      {serverInfo && <Grid item xs={12}><Alert severity="info">{serverInfo}</Alert></Grid>}
      {serverError && <Grid item xs={12}><Alert severity="error">{serverError}</Alert></Grid>}

      <Typography variant="h2">Title: {eventData.title}</Typography>

      <Accordion defaultExpanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Basic Information
            {!eventBasicInfoIsComplete && renderWarningIcon()}
            <BadgeEventChanges section="basicInfo" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <BasicInfoReview
            eventData={eventData}
            handleEventChange={handleEventChange}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            Event Info
            {!eventInfoIsComplete && renderWarningIcon()}
            <BadgeEventChanges section="eventInfo" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <EventInfoReview
            eventData={eventData}
            handleEventChange={handleEventChange}
          />
          <GoToSectionButton link={'/event/info/' + id} />
        </AccordionDetails>
      </Accordion>

      {!blockEvent && (
        <>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Speaker & Support Staff
            {!eventStaffIsComplete && renderWarningIcon()}
            <BadgeEventChanges section="speakerAndStaff" /></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SpeakerAndStaffSupportReview
            eventData={eventData}
            handleEventChange={handleEventChange}
          />
          <GoToSectionButton link={'/event/staff/' + id} />
        </AccordionDetails>
      </Accordion>

      {showAudience && (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Audience
            {!eventAudienceIsComplete && renderWarningIcon()}
            <BadgeEventChanges section="audience" /></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AudienceReview
            eventData={eventData}
            handleEventChange={handleEventChange}
          />
          <GoToSectionButton link={'/event/audience/' + id} />
        </AccordionDetails>
      </Accordion>
      )}
        </>
      )}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Dates & Times
            {!eventBookingIsComplete && renderWarningIcon()}
            <BadgeEventChanges section="datesAndTimes" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <DateAndTimesReview eventData={eventData} handleEventChange={handleEventChange} />
          <GoToSectionButton link={'/event/dateAndTime/' + id} />
        </AccordionDetails>
      </Accordion>

      {!blockEvent && (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Features & Requests
            {!eventFeaturesAndRequestsIsComplete && renderWarningIcon()}
            <BadgeEventChanges section="featuresAndRequests" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FeaturesAndRequestsReview eventData={eventData} handleEventChange={handleEventChange} />
          <GoToSectionButton link={'/event/featuresAndRequests/' + id} />
        </AccordionDetails>
      </Accordion>
      )}

      <FooterEventEdit
        eventData={eventData}
        setServerError={setServerError}
        setServerInfo={setServerInfo}
        setShowSuccessMessage={setShowSuccessMessage}
      />

    </Box>
    </EventValidationProvider>
  )
}